import IndicatorCategoryDot from "components/financials/IndicatorCategoryDot";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { motion } from "framer-motion";
import { matchSorter } from "match-sorter";
import { invariant } from "outvariant";
import Highlighter from "react-highlight-words";
import { useIntl } from "react-intl";
import removeAccents from "remove-accents";

interface ArbitraryIndicatorButtonProps {
  searchQuery?: string;
  isSelected?: boolean;
  displayIndicatorsCount?: number;
  indicator: IIndicatorFieldsFragment;
  disabled?: boolean;
  onClick?: () => void;
}
export default function ArbitraryIndicatorButton({
  searchQuery = "",
  isSelected,
  displayIndicatorsCount = 0,
  indicator,
  disabled,
  onClick,
}: ArbitraryIndicatorButtonProps) {
  const intl = useIntl();

  const localeLanguageCode = intl.locale === "pt-BR" ? "pt" : "en";
  invariant(indicator.documentation.length > 0, "Indicator is not documented");
  const documentation =
    indicator.documentation.find(({ languageCode }) => languageCode === localeLanguageCode) ||
    indicator.documentation[0];
  const normalizedWords = searchQuery
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean);
  const searchWords = [searchQuery, ...normalizedWords].filter((token) => token.length >= 3);

  const bestDocumentationHits = matchSorter(indicator.documentation, searchQuery, {
    keys: ["name", "abbreviation"],
    keepDiacritics: false,
    threshold: matchSorter.rankings.CONTAINS,
  });
  const bestDocumentation = bestDocumentationHits.length > 0 ? bestDocumentationHits[0] : documentation;
  const goodSubtitle =
    bestDocumentation.abbreviation !== bestDocumentation.name
      ? `${bestDocumentation.abbreviation}: ${bestDocumentation.description}`
      : bestDocumentation.abbreviation;

  return (
    <SimpleTextTooltip delayDuration={1200} content={documentation.description}>
      <motion.button
        key={indicator.objectId}
        type="button"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 0 }}
        onClick={onClick}
        disabled={disabled}
        className={classNames(
          "group",
          "flex w-full items-center justify-between rounded-2xl px-3 py-1 sm:rounded-xl",
          "border border-transparent",
          "bg-slate-50 text-slate-700 dark:bg-slate-900 dark:text-slate-300 sm:bg-transparent sm:dark:bg-transparent",
          "hover:bg-slate-100 hover:dark:border-slate-950 dark:hover:bg-teal-900/50",
          "dark:hover:text-teal-500",
          {
            "bg-violet-50/50 text-violet-600": isSelected,
            "dark:bg-violet-600/10 dark:text-violet-200": isSelected,
            "hover:dark:bg-violet-600/20 hover:dark:text-white": isSelected,
          },
          "disabled:cursor-not-allowed disabled:text-slate-500",
        )}
      >
        <div className="flex flex-col gap-y-1 py-2 text-left leading-6">
          <strong>
            <Highlighter
              className="line-clamp-2 text-sm font-bold group-disabled:text-slate-400 sm:text-xs"
              highlightClassName="bg-teal-800/5 text-teal-600 font-bold px-1.5 py-0.5 rounded-md dark:bg-teal-800/30 dark:text-teal-300"
              searchWords={searchWords}
              autoEscape={false}
              sanitize={removeAccents}
              textToHighlight={bestDocumentation.name}
            />
          </strong>
          <p
            className={classNames("sm:hidden", {
              "sm:flex": displayIndicatorsCount <= 2,
            })}
          >
            <Highlighter
              className="line-clamp-2 max-w-72 text-xs font-medium italic leading-5 tracking-wide text-slate-500 sm:text-slate-500 dark:sm:text-slate-400"
              highlightClassName="text-slate-600 font-bold bg-transparent dark:text-orange-300"
              searchWords={searchWords}
              autoEscape={false}
              sanitize={removeAccents}
              textToHighlight={goodSubtitle}
            />
          </p>
        </div>

        <span aria-hidden={true} className="w-12" />

        <div className="flex items-center space-x-2">
          {!indicator.viewerCanSee && (
            <strong
              className={classNames(
                "inline-flex whitespace-nowrap rounded-md bg-violet-50 px-1.5 py-0.5 text-xs font-bold leading-4 tracking-wider text-violet-600",
                "dark:bg-violet-950/50 dark:text-violet-300",
              )}
            >
              Premium
            </strong>
          )}

          <IndicatorCategoryDot indicator={indicator} />
        </div>
      </motion.button>
    </SimpleTextTooltip>
  );
}
